import React, { useState } from "react";
import { useGame } from "../context/GameContext";
import { useTimer } from "../hooks/useTimer";

const AVATARS = [
  { emoji: "🦊", bg: "#ff3e3e22", border: "#ff3e3e", text: "#ff3e3e" },
  { emoji: "👾", bg: "#9b6dff22", border: "#9b6dff", text: "#9b6dff" },
  { emoji: "🦖", bg: "#3effa322", border: "#3effa3", text: "#3effa3" },
  { emoji: "🐱", bg: "#f5c84222", border: "#f5c842", text: "#f5c842" },
  { emoji: "🦁", bg: "#ff6b3e22", border: "#ff6b3e", text: "#ff6b3e" },
  { emoji: "🐧", bg: "#3eaaff22", border: "#3eaaff", text: "#3eaaff" },
  { emoji: "🐷", bg: "#ff3eb522", border: "#ff3eb5", text: "#ff3eb5" },
  { emoji: "🐬", bg: "#3effee22", border: "#3effee", text: "#3effee" },
  { emoji: "👽", bg: "#aaff3e22", border: "#aaff3e", text: "#aaff3e" },
  { emoji: "👻", bg: "#ffffff22", border: "#ffffff", text: "#ffffff" },
];
const CIRCUMFERENCE = 2 * Math.PI * 24;

/**
 * MODO DESCUBRA A NOTA — Fluxo correto:
 * 1. NOTA_REVEAL: Todos os Dadores veem suas notas (em privado, um por vez)
 * 2. NOTA_DISCUSSAO: Todos jogam juntos — Adivinhadores fazem perguntas a seus Dadores simultaneamente
 * 3. NOTA_ADIVINHAR: Cada Adivinhador chuta a nota do seu parceiro (um por vez)
 * 4. NOTA_RESULTADO: Placar geral → vai para FinalScreen
 */

// ── 1. Revelação das notas (um Dador por vez) ─────────────────
export function NotaRevealScreen() {
  const { duplas, duplaAtualIdx, notaSecreta, jogadores, avancarRevealNota, setFase } = useGame();
  const [dadoresViram, setDadoresViram] = useState([]); // índices dos dadores que já viram
  const [dadoresVendo, setDadoresVendo] = useState(null); // dadorIdx que está vendo agora

  const totalDuplas = duplas.length;
  const dadores = duplas.map(d => d.dador);
  const todoViram = dadores.every(i => dadoresViram.includes(i));

  // Mostra tela de cada dador ver sua nota
  if (dadoresVendo !== null) {
    const duplaDesteD = duplas.find(d => d.dador === dadoresVendo);
    const av = AVATARS[dadoresVendo % AVATARS.length];
    const nomeAdiv = duplaDesteD ? jogadores[duplaDesteD.adivinhador] : "";
    // Nota é a mesma para todos nesta rodada (gerada no contexto)
    return (
      <div className="app-wrapper" style={{ paddingTop: 80 }}>
        <div className="card" style={{ maxWidth: 460 }}>
          <div className="card-header">
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 12 }}>
              <div style={{ width: 48, height: 48, borderRadius: "50%", background: av.bg, border: `2px solid ${av.border}`, color: av.text, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.6rem" }}>{av.emoji}</div>
              <div>
                <div style={{ fontFamily: "'Bebas Neue'", fontSize: "1.4rem" }}>{jogadores[dadoresVendo]}</div>
                <div style={{ color: "var(--text-muted)", fontSize: "0.75rem" }}>Dador de Dicas · sua nota secreta</div>
              </div>
            </div>
          </div>
          <div className="card-body" style={{ textAlign: "center" }}>
            <div style={{ fontSize: "0.72rem", color: "var(--gold)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 }}>🔐 Sua nota secreta</div>
            <div style={{ fontSize: "7rem", fontFamily: "'Bebas Neue'", color: "var(--gold)", lineHeight: 1 }}>{notaSecreta}</div>
            <div style={{ fontSize: "0.82rem", color: "var(--text-muted)", marginTop: 4 }}>de 10</div>
            <div style={{ marginTop: 20, padding: 14, background: "var(--bg-elevated)", borderRadius: 10, fontSize: "0.82rem", color: "var(--text-secondary)", lineHeight: 1.5 }}>
              💡 <strong style={{ color: "var(--text-primary)" }}>{nomeAdiv}</strong> vai te fazer perguntas de associação.
              Responda pensando nessa nota — sem revelar o número!
            </div>
          </div>
          <div className="card-footer">
            <button className="btn btn-primary btn-full" onClick={() => {
              setDadoresViram(prev => [...prev, dadoresVendo]);
              setDadoresVendo(null);
            }}>✅ Memorizei, fechar</button>
          </div>
        </div>
      </div>
    );
  }

  // Tela principal: lista de dadores para confirmar que viram
  return (
    <div className="app-wrapper" style={{ paddingTop: 80 }}>
      <div className="card" style={{ maxWidth: 500 }}>
        <div className="card-header">
          <div className="title-eyebrow">🎯 Descubra a Nota · Revelação</div>
          <h2 className="title-main" style={{ fontSize: "1.8rem" }}>Dadores,<br /><span style={{ color: "var(--gold)" }}>vejam suas notas!</span></h2>
          <p className="title-sub">
            Todos os Dadores recebem a <strong>mesma nota</strong> esta rodada.
            Cada um confirma que viu antes de prosseguir.
          </p>
        </div>
        <div className="card-body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {duplas.map((d, i) => {
            const avD = AVATARS[d.dador % AVATARS.length];
            const avA = AVATARS[d.adivinhador % AVATARS.length];
            const viu = dadoresViram.includes(d.dador);
            return (
              <div key={i} style={{ background: "var(--bg-elevated)", borderRadius: 14, padding: "14px 16px", border: `2px solid ${viu ? "var(--green)" : "var(--border)"}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <div style={{ background: "rgba(245,200,66,0.15)", border: "1px solid var(--gold)", color: "var(--gold)", borderRadius: 8, padding: "2px 10px", fontSize: "0.72rem", fontFamily: "'Space Mono'" }}>
                    DUPLA {i + 1}
                  </div>
                  {viu && <span style={{ color: "var(--green)", fontSize: "0.78rem" }}>✅ Viu a nota</span>}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "0.7rem", color: "var(--text-muted)", marginBottom: 4 }}>🎙️ Dador de Dicas</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: "1.3rem" }}>{avD.emoji}</span>
                      <span style={{ fontWeight: 700, color: avD.text }}>{jogadores[d.dador]}</span>
                    </div>
                  </div>
                  <span style={{ color: "var(--text-muted)", fontSize: "1.2rem" }}>↔️</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "0.7rem", color: "var(--text-muted)", marginBottom: 4 }}>🎯 Adivinhador</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: "1.3rem" }}>{avA.emoji}</span>
                      <span style={{ fontWeight: 700, color: avA.text }}>{jogadores[d.adivinhador]}</span>
                    </div>
                  </div>
                  {!viu && (
                    <button onClick={() => setDadoresVendo(d.dador)} style={{
                      background: "rgba(245,200,66,0.15)", border: "1px solid var(--gold)", color: "var(--gold)",
                      borderRadius: 10, padding: "8px 14px", cursor: "pointer", fontSize: "0.8rem", fontWeight: 700, whiteSpace: "nowrap",
                    }}>👁️ Ver nota</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="card-footer">
          <button className="btn btn-primary btn-full" disabled={!todoViram} onClick={avancarRevealNota}>
            🎤 Todos viram — iniciar perguntas →
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 2. Fase de discussão — TODAS as duplas jogam juntas ────────
export function NotaDiscussaoScreen() {
  const { duplas, jogadores, timerAtivo, tempoRodada, finalizarDiscussaoNota, notaSecreta } = useGame();
  const [tempoAcabou, setTempoAcabou] = useState(false);
  const { tempo, porcentagem, iniciar } = useTimer(tempoRodada, () => setTempoAcabou(true));
  React.useEffect(() => { if (timerAtivo) iniciar(); }, []);
  const timerColor = porcentagem > 60 ? "var(--green)" : porcentagem > 30 ? "var(--gold)" : "var(--accent)";

  return (
    <div className="app-wrapper" style={{ paddingTop: 80, paddingBottom: 40 }}>
      <div className="card" style={{ maxWidth: 560 }}>
        <div className="card-header">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div className="title-eyebrow">🎯 Descubra a Nota · Perguntas</div>
              <h2 className="title-main" style={{ fontSize: "1.8rem" }}>Todos<br /><span style={{ color: "var(--gold)" }}>jogam juntos!</span></h2>
            </div>
            {timerAtivo && (
              <div style={{ textAlign: "center" }}>
                <svg viewBox="0 0 58 58" width="64" height="64">
                  <circle cx="29" cy="29" r="24" stroke="var(--border)" strokeWidth="4" fill="none" />
                  <circle cx="29" cy="29" r="24" stroke={timerColor} strokeWidth="4" fill="none"
                    strokeDasharray={CIRCUMFERENCE}
                    strokeDashoffset={CIRCUMFERENCE - (CIRCUMFERENCE * porcentagem) / 100}
                    strokeLinecap="round" transform="rotate(-90 29 29)" />
                </svg>
                <div style={{ fontFamily: "'Bebas Neue'", fontSize: "1.4rem", color: timerColor, marginTop: -8 }}>{tempo}s</div>
              </div>
            )}
          </div>
          <p className="title-sub" style={{ marginTop: 8 }}>
            Cada Adivinhador faz perguntas ao seu Dador ao mesmo tempo que os outros.
            No final, cada um tenta adivinhar a nota!
          </p>
        </div>
        <div className="card-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>

          {/* Duplas jogando */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {duplas.map((d, i) => {
              const avD = AVATARS[d.dador % AVATARS.length];
              const avA = AVATARS[d.adivinhador % AVATARS.length];
              return (
                <div key={i} style={{ background: "var(--bg-elevated)", borderRadius: 12, padding: "14px 16px", border: "1px solid var(--border)" }}>
                  <div style={{ fontSize: "0.7rem", color: "var(--gold)", fontWeight: 700, textTransform: "uppercase", marginBottom: 10 }}>
                    Dupla {i + 1}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
                    <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: "1.3rem" }}>{avA.emoji}</span>
                      <div>
                        <div style={{ fontWeight: 700, color: avA.text, fontSize: "0.88rem" }}>{jogadores[d.adivinhador]}</div>
                        <div style={{ color: "var(--text-muted)", fontSize: "0.68rem" }}>faz perguntas ➜</div>
                      </div>
                    </div>
                    <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontWeight: 700, color: avD.text, fontSize: "0.88rem" }}>{jogadores[d.dador]}</div>
                        <div style={{ color: "var(--text-muted)", fontSize: "0.68rem" }}>responde com dicas</div>
                      </div>
                      <span style={{ fontSize: "1.3rem" }}>{avD.emoji}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Dicas de perguntas */}
          <div style={{ background: "rgba(245,200,66,0.1)", border: "1px solid var(--gold)", borderRadius: 12, padding: "14px 18px" }}>
            <div style={{ fontSize: "0.75rem", color: "var(--gold)", fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>
              💡 Exemplos de perguntas que os Adivinhadores podem fazer
            </div>
            <div style={{ fontSize: "0.84rem", color: "var(--text-secondary)", lineHeight: 1.8 }}>
              • "Qual lugar tem essa nota?"<br />
              • "Qual comida tem essa nota?"<br />
              • "Que celebridade tem essa nota?"<br />
              • "Que filme tem essa nota?"<br />
              • "Que experiência tem essa nota?"
            </div>
          </div>

          {tempoAcabou && (
            <div style={{ background: "rgba(245,200,66,0.15)", border: "1px solid var(--gold)", borderRadius: 12, padding: "14px 18px", textAlign: "center" }}>
              <div style={{ fontFamily: "'Bebas Neue'", fontSize: "1.4rem", color: "var(--gold)" }}>⏰ Tempo esgotado!</div>
              <div style={{ fontSize: "0.84rem", color: "var(--text-secondary)", marginTop: 4 }}>Hora de cada um chutar sua nota!</div>
            </div>
          )}
        </div>
        <div className="card-footer">
          <button className="btn btn-primary" onClick={finalizarDiscussaoNota}>
            🎯 Hora de adivinhar →
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 3. Adivinhação — cada Adivinhador chuta (um por vez) ────────
export function NotaAdivinharScreen() {
  const { duplas, duplaAtualIdx, jogadores, registrarChute } = useGame();
  const [chute, setChute] = useState(null);
  const dupla = duplas[duplaAtualIdx];
  if (!dupla) return null;
  const nomeAdiv = jogadores[dupla.adivinhador];
  const nomeDador = jogadores[dupla.dador];
  const avA = AVATARS[dupla.adivinhador % AVATARS.length];
  const avD = AVATARS[dupla.dador % AVATARS.length];

  return (
    <div className="app-wrapper" style={{ paddingTop: 80 }}>
      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <div style={{ color: "var(--text-muted)", fontSize: "0.75rem", textTransform: "uppercase", marginBottom: 8 }}>
          Adivinhador {duplaAtualIdx + 1} de {duplas.length}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16 }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: avA.bg, border: `3px solid ${avA.border}`, color: avA.text, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.8rem", margin: "0 auto 6px", boxShadow: `0 0 20px ${avA.border}44` }}>{avA.emoji}</div>
            <div style={{ fontWeight: 700, color: avA.text, fontSize: "0.88rem" }}>{nomeAdiv}</div>
            <div style={{ color: "var(--text-muted)", fontSize: "0.7rem" }}>Adivinhador</div>
          </div>
          <div style={{ fontSize: "1.4rem", color: "var(--text-muted)" }}>→</div>
          <div style={{ textAlign: "center" }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: avD.bg, border: `3px solid ${avD.border}`, color: avD.text, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.8rem", margin: "0 auto 6px", boxShadow: `0 0 20px ${avD.border}44` }}>{avD.emoji}</div>
            <div style={{ fontWeight: 700, color: avD.text, fontSize: "0.88rem" }}>{nomeDador}</div>
            <div style={{ color: "var(--text-muted)", fontSize: "0.7rem" }}>Dador</div>
          </div>
        </div>
      </div>

      <div className="card" style={{ maxWidth: 480 }}>
        <div className="card-header">
          <div className="title-eyebrow">🎯 Descubra a Nota · Adivinhação</div>
          <h2 className="title-main" style={{ fontSize: "1.8rem" }}>
            {nomeAdiv},<br /><span style={{ color: "var(--gold)" }}>qual é a nota?</span>
          </h2>
          <p className="title-sub">Selecione o número que você acha que {nomeDador} recebeu.</p>
        </div>
        <div className="card-body">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10 }}>
            {[1,2,3,4,5,6,7,8,9,10].map(n => (
              <button key={n} onClick={() => setChute(n)} style={{
                aspectRatio: "1", borderRadius: 12,
                border: `2px solid ${chute === n ? "var(--gold)" : "var(--border)"}`,
                background: chute === n ? "rgba(245,200,66,0.2)" : "var(--bg-elevated)",
                color: chute === n ? "var(--gold)" : "var(--text-primary)",
                fontFamily: "'Bebas Neue'", fontSize: "1.6rem", cursor: "pointer",
                transition: "all 0.15s",
                boxShadow: chute === n ? "0 0 16px rgba(245,200,66,0.3)" : "none",
              }}>{n}</button>
            ))}
          </div>
        </div>
        <div className="card-footer">
          <button className="btn btn-primary btn-full" disabled={chute === null} onClick={() => registrarChute(chute)}>
            🎯 Confirmar: Nota {chute ?? "?"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 4. Resultado parcial / final ───────────────────────────────
export function NotaResultadoScreen() {
  const { duplas, duplaAtualIdx, jogadores, notaSecreta, notaChute, notaResultados, proximaDuplaNota } = useGame();
  const dupla = duplas[duplaAtualIdx];
  const acertou = notaChute === notaSecreta;
  const nomeDador = dupla ? jogadores[dupla.dador] : "";
  const nomeAdiv = dupla ? jogadores[dupla.adivinhador] : "";
  const proximaExiste = duplaAtualIdx + 1 < duplas.length;

  return (
    <div className="app-wrapper" style={{ paddingTop: 80 }}>
      <div className="card" style={{ maxWidth: 480 }}>
        <div className="card-header" style={{ textAlign: "center" }}>
          <div style={{ fontSize: "4rem", marginBottom: 10 }}>{acertou ? "🎉" : "😅"}</div>
          <h2 className="title-main" style={{ fontSize: "2rem", color: acertou ? "var(--green)" : "var(--accent)" }}>
            {acertou ? "Acertou!" : "Errou!"}
          </h2>
          <p className="title-sub">{nomeAdiv} {acertou ? "acertou em cheio" : "não acertou"} a nota de {nomeDador}!</p>
        </div>
        <div className="card-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1, background: "rgba(245,200,66,0.12)", border: "1px solid var(--gold)", borderRadius: 14, padding: "18px 16px", textAlign: "center" }}>
              <div style={{ fontSize: "0.72rem", color: "var(--gold)", textTransform: "uppercase", marginBottom: 8 }}>🔐 Nota real</div>
              <div style={{ fontSize: "3.6rem", fontFamily: "'Bebas Neue'", color: "var(--gold)" }}>{notaSecreta}</div>
            </div>
            <div style={{ flex: 1, background: acertou ? "rgba(62,255,163,0.12)" : "rgba(255,62,62,0.12)", border: `1px solid ${acertou ? "var(--green)" : "var(--accent)"}`, borderRadius: 14, padding: "18px 16px", textAlign: "center" }}>
              <div style={{ fontSize: "0.72rem", color: acertou ? "var(--green)" : "var(--accent)", textTransform: "uppercase", marginBottom: 8 }}>🎯 Chute</div>
              <div style={{ fontSize: "3.6rem", fontFamily: "'Bebas Neue'", color: acertou ? "var(--green)" : "var(--accent)" }}>{notaChute}</div>
            </div>
          </div>

          {/* Mini placar acumulado */}
          <div style={{ background: "var(--bg-elevated)", borderRadius: 12, padding: "14px 16px" }}>
            <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 10 }}>📊 Placar até agora</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {notaResultados.map((r, i) => {
                const avA = AVATARS[r.adivinhador % AVATARS.length];
                return (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, background: r.acertou ? "rgba(62,255,163,0.12)" : "rgba(255,62,62,0.12)", border: `1px solid ${r.acertou ? "var(--green)" : "var(--accent)"}`, borderRadius: 20, padding: "5px 12px" }}>
                    <span style={{ fontSize: "1rem" }}>{avA.emoji}</span>
                    <span style={{ fontSize: "0.78rem", fontWeight: 700, color: r.acertou ? "var(--green)" : "var(--accent)" }}>{jogadores[r.adivinhador]}</span>
                    <span style={{ fontSize: "0.9rem" }}>{r.acertou ? "✅" : "❌"}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="card-footer">
          <button className="btn btn-primary btn-full" onClick={proximaDuplaNota}>
            {proximaExiste ? `Próximo: ${jogadores[duplas[duplaAtualIdx + 1]?.adivinhador]} adivinhar →` : "🏆 Ver placar final →"}
          </button>
        </div>
      </div>
    </div>
  );
}
